import { useState, ReactElement, CSSProperties } from 'react';
import { NavLink } from 'react-router-dom';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Column,
    Base,
    PRODUCT_TYPE_COLLECTION_NAME,
    SECTION_COLLECTION_NAME,
    COVERAGE_COLLECTION_NAME,
    Language,
    InputListItem,
    Clause,
    USER_PROFILE_COLLECTION_NAME,
    userProfileCoverter,
    UserProfile,
} from 'models';
import {
    collection,
    query,
    orderBy,
    doc,
    addDoc,
    deleteDoc,
    OrderByDirection,
    FirestoreDataConverter,
    QueryConstraint,
    updateDoc,
    where,
} from 'firebase/firestore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import Modal from 'react-modal';
import { db, useAuthState } from 'firebaseStore';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Button, Cancel } from 'components/button';
import { Autocomplete, TextField } from '@mui/material';
import { CollectionProvider, CollectionState, useCollectionContext } from './collectionContext';
import './settings.css';
import { LanguageCompletion } from './languageCompletion';
import { useCommit } from 'commits';

Modal.setAppElement('#modal');
const Direction = ({ direction }: { direction: OrderByDirection }) =>
    direction === 'desc' ? (
        <KeyboardArrowDownIcon
            style={{ marginTop: '4px' }}
            className="direction"
            sx={{ fontSize: 14 }}
        />
    ) : (
        <KeyboardArrowUpIcon
            style={{ marginTop: '4px' }}
            className="direction"
            sx={{ fontSize: 14 }}
        />
    );

const OrderByField = ({
    direction,
    label,
    selected,
    onClick,
    headers,
}: Column & { onClick: () => void; headers: string }) => (
    <div
        className={`orderByField ${selected ? 'selected' : ''} ${
            headers === 'columns' ? 'orderByFieldColumns' : ''
        }`}
        onClick={onClick}
    >
        {label}
        <Direction direction={direction as OrderByDirection} />
    </div>
);

export const getNextFields = (field: Column, fields: Column[]) =>
    fields.map((field_) => {
        if (field_.field === field.field) {
            if (!field_.selected) {
                field_.selected = true;
                return field_;
            }
            field_.direction = field_.direction === 'desc' ? 'asc' : 'desc';
            return field_;
        }
        field_.selected = false;
        return field_;
    });

export const OrderBy = ({
    columns,
    onFieldClick,
    headers,
}: {
    columns: Column[];
    onFieldClick: (_: Column) => void;
    headers: string;
}) => {
    const createdAt = columns.find((c) => c.fieldType === 'createdAt');
    return (
        <div className="orderBy" style={{ paddingLeft: createdAt ? undefined : '22px' }}>
            {createdAt ? (
                <div style={{ width: '65px', display: 'flex', justifyContent: 'center' }}>
                    <OrderByField
                        {...createdAt}
                        onClick={() => onFieldClick(createdAt)}
                        headers={'hide'}
                    />
                </div>
            ) : null}
            {columns.map((column) =>
                column.fieldType === 'createdAt' || !column.column ? null : column.direction ? (
                    <OrderByField
                        key={column.field}
                        {...column}
                        onClick={() => onFieldClick(column)}
                        headers={headers}
                    />
                ) : column.fieldType === 'reviewStatus' ? (
                    <div key="reviewStatus" style={{ padding: '0 6px' }}>
                        {column.label}
                    </div>
                ) : (
                    <div
                        key={column.field}
                        className={
                            column.field === 'completion'
                                ? 'completionColumn'
                                : 'orderByFieldColumns'
                        }
                        style={{ paddingLeft: '22px' }}
                    >
                        {column.label}
                    </div>
                )
            )}
        </div>
    );
};

interface AdditionalStyles {
    content?: CSSProperties;
    overlay?: CSSProperties;
}
export const customStyles = (indexOffset?: number, additionalStyles?: AdditionalStyles) => {
    return {
        content: {
            zIndex: 1001 + (indexOffset || 0),
            width: '518px',
            height: '',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            fontFamily: 'Abel',
            padding: '0',
            backgroundColor: '#fbfbfb',
            ...(additionalStyles?.content || {}),
        },
        overlay: { zIndex: 1000 + (indexOffset || 0), ...(additionalStyles?.overlay || {}) },
    };
};

const Input = ({
    label,
    required,
    value,
    onChange,
    fieldType,
}: {
    label: string;
    required: boolean;
    value: string;
    onChange: (_: string) => void;
    fieldType?: 'boolean' | 'createdAt' | 'subset' | 'reviewStatus';
}) => {
    if (fieldType === 'boolean') {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: '8px',
                    marginLeft: '-3px',
                }}
            >
                <input
                    type="checkbox"
                    checked={!!value}
                    onChange={() => onChange(value ? '' : 'on')}
                    placeholder=""
                />
                <div className="label">
                    {label}
                    {required ? '*' : null}
                </div>
            </div>
        );
    }
    return (
        <div>
            <div className="label">
                {label}
                {required ? '*' : null}
            </div>
            <textarea
                className="input"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder=""
            />
        </div>
    );
};

export interface InputFromListProps {
    onSelect: (_?: InputListItem) => void;
    value: InputListItem;
    values: InputListItem[];
    id?: string;
}
export const InputFromList = ({ id, value, onSelect, values }: InputFromListProps) => {
    return (
        <FormControl fullWidth>
            <Select
                id={id}
                variant="filled"
                value={value.name}
                onChange={(e) => onSelect(values.find((value_) => value_.name === e.target.value))}
                className="select"
            >
                {values.map((value_) => (
                    <MenuItem value={value_.name} key={value_.id}>
                        {value_.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

interface InputFromCollectionProps {
    collectionName: string;
    onSelect: (_?: string) => void;
    value?: string;
}
export const InputFromCollection = ({
    collectionName,
    onSelect,
    value,
}: InputFromCollectionProps) => {
    const [records, pending] = useCollectionContext(collectionName);
    if (pending) {
        return null;
    }
    return (
        <FormControl fullWidth>
            <Select
                id={collectionName}
                variant="filled"
                value={value || ''}
                onChange={(e) => onSelect(e.target.value)}
                className="select"
            >
                {records?.map((record) => (
                    <MenuItem value={record.indexId()} key={record.indexId()}>
                        {record.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

interface SubsetDisplayProps {
    indexIds: string[];
    collectionName: string;
}
const SubsetDisplay = ({ indexIds, collectionName }: SubsetDisplayProps) => {
    const [, , , recordMap] = useCollectionContext(collectionName);
    return (
        <div>
            {indexIds
                .map((indexId) => {
                    const record = recordMap.get(indexId);
                    if (!record) {
                        return '';
                    }
                    return record.name;
                })
                .filter((v) => v)
                .join(', ')}
        </div>
    );
};

const ReviewStatusMessages = {
    approved: 'Approved',
    sent: 'In Review',
    returned: 'Sent Back',
    '': 'Not Reviewed',
};
const ReviewStatusColors = {
    approved: '#18A0FB',
    sent: '#FB9218',
    returned: '#FB9218',
    '': '#D8D8D8',
};
interface SourceDisplayProps {
    indexId: string;
    collectionName: string;
}
const SourceDisplay = ({ indexId, collectionName }: SourceDisplayProps) => {
    const [, , , recordMap] = useCollectionContext(collectionName);
    const record = recordMap.get(indexId);
    const name = record?.name || indexId;
    return <div>{name}</div>;
};

const ReviewStatus = ({ record }: { record: Clause }) => (
    <div
        style={{
            height: '32px',
            width: '99px',
            backgroundColor: ReviewStatusColors[record.reviewStatus],
            borderRadius: '15px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#FFFFFF',
            fontWeight: 700,
        }}
    >
        {ReviewStatusMessages[record.reviewStatus]}
    </div>
);

export interface SendToReviewProps {
    selectedClauses: Clause[];
    setModalIsOpen: (_: boolean) => void;
    onUpdate: (data: Base, message: string) => void;
}
export const SendToReview = (props: SendToReviewProps) => (
    <CollectionProvider
        collectionName={USER_PROFILE_COLLECTION_NAME}
        converter={userProfileCoverter}
    >
        <SendToReviewModal {...props} />
    </CollectionProvider>
);
export const SendToReviewModal = ({
    selectedClauses,
    setModalIsOpen,
    onUpdate,
}: SendToReviewProps) => {
    const [userProfiles] = useCollectionContext(
        USER_PROFILE_COLLECTION_NAME
    ) as CollectionState<UserProfile>;
    const [reviewers, setReviewer] = useState<UserProfile[]>([]);
    return (
        <div>
            <div className="formLabel">Send to Review</div>
            <div className="formInputs">
                Please select the person you want to send these clasues for review. If they are not
                in your organisation, they only will be able to see and review the clauses that you
                selected
            </div>
            <div style={{ padding: '10px 20px 20px 20px' }}>
                <InputMultipleFromCollection
                    collectionName={USER_PROFILE_COLLECTION_NAME}
                    value={reviewers.map((r) => r.indexId())}
                    onSelect={(newValue) => {
                        setReviewer(
                            userProfiles.filter((r) => newValue?.find((sid) => sid === r.indexId()))
                        );
                    }}
                    filter={(up: Base) => !!up.roles.find((r: string) => r === 'reviewer')}
                />
            </div>
            <div className="formInputs">
                Add reviewers{' '}
                <NavLink to="/users" target="_blank">
                    here
                </NavLink>
            </div>
            <div className="formButtons">
                <Cancel
                    onClick={() => {
                        setModalIsOpen(false);
                    }}
                />
                <Button
                    style={{ width: '149px' }}
                    theme={reviewers.length ? undefined : 'disabled'}
                    onClick={
                        reviewers.length
                            ? () => {
                                  selectedClauses.map((clause) =>
                                      onUpdate(
                                          new Clause({
                                              ...clause,
                                              reviewStatus: 'sent',
                                              reviewedBy: [],
                                              reviewers: reviewers.map((r) => r.indexId()),
                                          }),
                                          ''
                                      )
                                  );
                                  setModalIsOpen(false);
                              }
                            : undefined
                    }
                >
                    Send
                </Button>
            </div>
        </div>
    );
};
export interface InputMultipleProps {
    onSelect: (_?: string[]) => void;
    value: string[];
    values: string[];
    id?: string;
    getName: (_?: string) => string;
}
export const InputMultiple = ({ id, value, onSelect, values, getName }: InputMultipleProps) => {
    return (
        <FormControl fullWidth>
            <Select
                id={id}
                variant="filled"
                value={value}
                onChange={(e) => onSelect((e.target.value as string[]) || [])}
                className="select"
                multiple
            >
                {values.map((value_) => (
                    <MenuItem value={value_} key={value_}>
                        {getName(value_)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

interface InputMultipleFromCollectionProps {
    collectionName: string;
    onSelect: (_?: string[]) => void;
    value?: string[];
    filter?: (_: Base) => boolean;
}
export const InputMultipleFromCollection = ({
    collectionName,
    onSelect,
    value,
    filter,
}: InputMultipleFromCollectionProps) => {
    const [records_] = useCollectionContext(collectionName);
    const records = !records_?.length ? [] : filter ? records_.filter(filter) : records_;
    console.log('InputMultipleFromCollection value', value);
    return (
        <FormControl fullWidth>
            <Select
                id={collectionName}
                variant="filled"
                value={value}
                onChange={(e) => onSelect((e.target.value as string[]) || [])}
                className="select"
                multiple
            >
                {records.map((record) => (
                    <MenuItem value={record.indexId()} key={record.indexId()}>
                        {record.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

interface AutoCollectionProps<T> {
    records: T[];
    label: string;
}
export const AutoCollection = <T extends Base>({ records, label }: AutoCollectionProps<T>) => {
    return (
        <Autocomplete
            options={records.map((r: T) => {
                return { label: r.name };
            })}
            renderInput={(params) => <TextField {...params} label={label} />}
        />
    );
};

export interface ConfirmProps {
    title: string;
    text: string;
    setModalIsOpen: (_: boolean) => void;
    onClick: (_: string) => void;
    actionLabel: string;
    withMessage?: boolean;
}

export const Confirm = ({
    title,
    text,
    setModalIsOpen,
    onClick,
    actionLabel,
    withMessage,
}: ConfirmProps) => {
    const [reason, setReason] = useState('');
    const enabled = !withMessage || reason;
    return (
        <div>
            <div className="formLabel">{title}</div>
            <div className="formInputs">{text}</div>
            {withMessage && (
                <div className="formInputs">
                    <Input value={reason} onChange={setReason} label="Reason" required={true} />
                </div>
            )}
            <div className="formButtons">
                <Cancel
                    onClick={() => {
                        setReason('');
                        setModalIsOpen(false);
                    }}
                />
                <Button
                    theme={enabled ? 'red' : 'disabled'}
                    style={{ width: '149px' }}
                    onClick={enabled ? () => onClick(reason) : undefined}
                >
                    {actionLabel}
                </Button>
            </div>
        </div>
    );
};

interface CreateProps<T> {
    label: string;
    name: string;
    columns: Column[];
    setModalIsOpen: (_?: T) => void;
    onSave: (_: T, reason: string) => void;
    initialState: T;
}
export const Create = <T extends Base>({
    label,
    name,
    columns,
    setModalIsOpen,
    onSave,
    initialState,
}: CreateProps<T>) => {
    const [values, setValues] = useState<T>(initialState);
    const [confirmProps, setConfirmProps] = useState<ConfirmProps>();
    return (
        <div>
            <Modal
                isOpen={!!confirmProps}
                style={customStyles(2)}
                onRequestClose={() => setConfirmProps(undefined)}
            >
                {confirmProps ? <Confirm {...confirmProps} /> : null}
            </Modal>
            <div className="formLabel">{initialState.id ? `Update a ${name}` : `New ${label}`}</div>
            <div className="formInputs">
                <form>
                    {columns.map((column) => {
                        if (!column.source) {
                            return (
                                <Input
                                    key={column.field}
                                    value={values[column.field] || ''}
                                    onChange={(value) =>
                                        setValues({ ...values, [column.field]: value })
                                    }
                                    label={column.label}
                                    required={!!column.required}
                                    fieldType={column.fieldType}
                                />
                            );
                        }
                        if (column.source === PRODUCT_TYPE_COLLECTION_NAME) {
                            return (
                                <div key={column.field}>
                                    <div className="label">
                                        {column.label}
                                        {column.required ? '*' : null}
                                    </div>
                                    {column.fieldType === 'subset' ? (
                                        <InputMultipleFromCollection
                                            collectionName={PRODUCT_TYPE_COLLECTION_NAME}
                                            value={values[column.field]}
                                            onSelect={(value) =>
                                                setValues({
                                                    ...values,
                                                    [column.field]: value,
                                                })
                                            }
                                        />
                                    ) : (
                                        <InputFromCollection
                                            collectionName={PRODUCT_TYPE_COLLECTION_NAME}
                                            value={values[column.field]}
                                            onSelect={(value) =>
                                                setValues({ ...values, [column.field]: value })
                                            }
                                        />
                                    )}
                                </div>
                            );
                        }
                        if (column.source === SECTION_COLLECTION_NAME) {
                            return (
                                <div key={column.field}>
                                    <div className="label">
                                        {column.label}
                                        {column.required ? '*' : null}
                                    </div>
                                    <InputFromCollection
                                        collectionName={SECTION_COLLECTION_NAME}
                                        value={values[column.field]}
                                        onSelect={(value) =>
                                            setValues({ ...values, [column.field]: value })
                                        }
                                    />
                                </div>
                            );
                        }
                        if (column.source === COVERAGE_COLLECTION_NAME) {
                            // should display if the clause's section is a coverage section
                        }
                        return <div key="none"></div>;
                    })}
                </form>
            </div>
            <div className="formButtons">
                <Cancel onClick={() => setModalIsOpen(undefined)} />
                <Button
                    style={{ width: '149px' }}
                    onClick={() =>
                        initialState.id
                            ? setConfirmProps({
                                  title: `You are about to update this ${name}`,
                                  text: `This ${name} will be updated, contracts that contain this element will be updated. Are you sure you want to continue?`,
                                  onClick: (message) => onSave(values, message),
                                  setModalIsOpen: () => setConfirmProps(undefined),
                                  actionLabel: 'continue',
                                  withMessage: true,
                              })
                            : onSave(values, '')
                    }
                >
                    {initialState.id ? 'Update' : 'Save'}
                </Button>
            </div>
        </div>
    );
};

export interface RecordMap<T> {
    [key: string]: T;
}
export const useRecordMapState = <T extends Base>(value: RecordMap<T>) => {
    return useState(value);
};

export interface ItemInputProps extends React.ComponentPropsWithoutRef<'input'> {
    onChange: () => void;
}
const ItemInput = (props: ItemInputProps) => () => <input {...props} />;

const Div = ({ children, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
    <div {...props}>{children}</div>
);

const languageContainer =
    (language: Language) =>
    ({ children, ...props }: React.ComponentPropsWithoutRef<'div'>) =>
        (
            <NavLink
                to={`/translations/${language.id}`}
                style={{ textDecoration: 'unset', color: 'unset' }}
            >
                <div {...props}>{children}</div>
            </NavLink>
        );

export interface ListProps<T> {
    columns: Column[];
    records?: T[];
    selectedRecords: string[];
    setSelectedRecords: (_: string[], __?: T) => void;
    itemInput?: (_: ItemInputProps) => () => ReactElement | null;
    minHeight?: string;
    recordsState: T[];
    setRecordsState: (_: T[]) => void;
    onEditItem?: (_: T) => void;
    onDeleteItem?: (_: T) => void;
    onDuplicateItem?: (_: T) => void;
}
export const List = <T extends Base>({
    columns,
    records,
    selectedRecords,
    setSelectedRecords,
    itemInput,
    minHeight,
    onEditItem,
    onDeleteItem,
    onDuplicateItem,
}: ListProps<T>) => {
    const displayColumns = columns.filter((c) => c.column).length;
    const leftColumns = columns.filter((c) => c.column === 'left').length;
    const rightColumns = columns.filter((c) => c.column === 'right').length;
    return (
        <div className="listRecords" style={minHeight ? { minHeight } : undefined}>
            {records?.map((record) => {
                const Container = record.__type === 'Language' ? languageContainer(record) : Div;
                return (
                    <div key={record.id} className="itemRecord">
                        <div
                            className="itemRecordSection"
                            style={{ width: `${(100 * leftColumns) / displayColumns}%` }}
                        >
                            {itemInput
                                ? itemInput({
                                      type: 'checkbox',
                                      className: 'itemSelect',
                                      key: 'select',
                                      checked: !!selectedRecords.find((i) => i === record.id),
                                      style: { zIndex: 10, cursor: 'pointer' },
                                      onChange: () => {
                                          selectedRecords.find((i) => i === record.id)
                                              ? setSelectedRecords(
                                                    selectedRecords.filter((i) => i !== record.id),
                                                    record
                                                )
                                              : setSelectedRecords(
                                                    selectedRecords.concat([record.id]),
                                                    record
                                                );
                                      },
                                  })()
                                : null}
                            {columns
                                .filter((column: Column) => column.column === 'left')
                                .map((column: Column) => {
                                    return (
                                        <div
                                            className={
                                                column.fieldType === 'reviewStatus'
                                                    ? undefined
                                                    : 'item'
                                            }
                                            key={column.field}
                                        >
                                            {column.fieldType === 'subset' ? (
                                                <SubsetDisplay
                                                    indexIds={record[column.field]}
                                                    collectionName={column.source as string}
                                                ></SubsetDisplay>
                                            ) : column.fieldType === 'reviewStatus' ? (
                                                <ReviewStatus
                                                    record={record as unknown as Clause}
                                                />
                                            ) : column.source ? (
                                                <SourceDisplay
                                                    indexId={record[column.field] as string}
                                                    collectionName={column.source as string}
                                                ></SourceDisplay>
                                            ) : column.field === 'name' ? (
                                                <Container>{record[column.field]}</Container>
                                            ) : (
                                                record[column.field]
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                        <div
                            className="itemRecordSection"
                            style={{ width: `${(100 * rightColumns) / displayColumns}%` }}
                        >
                            {columns
                                .filter((column: Column) => column.column === 'right')
                                .map((column: Column) => {
                                    if (column.field === 'completion') {
                                        return (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <div style={{ width: '140px' }}>
                                                    <LanguageCompletion
                                                        key={record.id}
                                                        languageIndexId={record.indexId()}
                                                    />
                                                </div>{' '}
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="item" key={column.field}>
                                            {column.fieldType === 'boolean' && record[column.field]
                                                ? column.label
                                                : record[column.field]}
                                        </div>
                                    );
                                })}
                        </div>
                        <div className="itemRecordEdit">
                            {onDuplicateItem ? (
                                <ContentCopyIcon
                                    style={{ cursor: 'pointer', zIndex: 10 }}
                                    onClick={() => onDuplicateItem(record)}
                                />
                            ) : null}
                            {onEditItem ? (
                                <EditIcon
                                    style={{ cursor: 'pointer', zIndex: 10 }}
                                    onClick={() => onEditItem(record)}
                                />
                            ) : null}
                            {onDeleteItem ? (
                                <DeleteOutlineIcon
                                    style={{ cursor: 'pointer', zIndex: 10 }}
                                    onClick={() => onDeleteItem(record)}
                                />
                            ) : null}
                            {onEditItem || onDeleteItem || onDuplicateItem ? (
                                <div style={{ width: '8px' }}></div>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
List.defaultProps = {
    itemInput: ItemInput,
    recordsState: [],
    setRecordsState: () => {},
};

export const renderList =
    <T extends Base>({ records, ...props }: ListProps<T>) =>
    () =>
        <List records={records || []} {...props} />;

export declare type ListHeadersType = 'columns' | 'actions' | 'hide';

const constraints = (tenant: string, columns: Column[]): QueryConstraint[] => {
    const orderColumn = columns.find((column) => column.selected);
    if (!orderColumn) {
        return [where('tenantId', '==', tenant)];
    }
    if (orderColumn.index) {
        return [
            where(orderColumn.index as string, '>=', tenant),
            where(orderColumn.index, '<', tenant.replace(/.$/, '~')),
            orderBy(orderColumn.index, orderColumn.direction),
        ];
    }
    return [where('tenantId', '==', tenant), orderBy(orderColumn.field, orderColumn.direction)];
};

interface CollectionListProps<T> {
    converter: FirestoreDataConverter<T>;
    collectionName: string;
    columns: Column[];
    label: string;
    name: string;
    nextTo?: string;
    getNew?: (_: any) => T;
    list: (_: ListProps<T>) => () => ReactElement | null;
    withDelete: boolean;
    addTo?: string;
    actions?: boolean;
    headers: ListHeadersType;
    onSelect?: (_: string[]) => void;
    historyTo?: string;
}
export const CollectionList = <T extends Base>({
    converter,
    collectionName,
    columns,
    label,
    name,
    nextTo,
    getNew,
    list,
    withDelete,
    addTo,
    actions,
    headers,
    onSelect,
    historyTo,
}: CollectionListProps<T>) => {
    const [fields, setFields] = useState<Column[]>(columns.filter((column) => column.filter));
    const [modalIsOpen, setModalIsOpen] = useState<T>();
    const [reviewIsOpen, setReviewIsOpen] = useState(false);
    const [editRecord, setEditRecord] = useState<T>();
    const [, , tenant] = useAuthState();
    const [confirmProps, setConfirmProps] = useState<ConfirmProps>();
    const [selectedRecords, _setSelectedRecords] = useState<string[]>([]);
    const setSelectedRecords = (_selectedRecords: string[]) => {
        _setSelectedRecords(_selectedRecords);
        onSelect && onSelect(_selectedRecords);
    };
    const [recordsState, setRecordsState] = useState<T[]>([]);
    const collectionRef = collection(db, collectionName).withConverter(converter);
    const recordQuery = query(collectionRef, ...constraints(tenant, fields));
    const [records] = useCollectionData<T>(recordQuery);
    const commit = useCommit<T>();
    const onFieldClick = (field: Column) => setFields(getNextFields(field, fields));
    const onUpdate = async (data: Base, message: string) => {
        console.log('onUpdate data', data);
        if (!getNew) {
            throw new Error('no getNew');
        }
        const record = getNew(data);
        const previous = records?.find((r) => r.id === data.id) as T;
        console.log('onUpdate previous', previous);

        const current = getNew({ ...previous, ...data });
        await commit({ previous, current, action: 'update', message });
        console.log('onUpdate updateDoc', collectionName, data.id);
        updateDoc(doc(db, collectionName, data.id), record.toFirestore()).then(() =>
            setEditRecord(undefined)
        );
    };
    const onSave = (data: Base, message: string) => {
        if (!getNew) {
            throw new Error('no getNew');
        }
        const missing = columns.find((column) => column.required && !data[column.field]);
        if (missing) {
            return Promise.reject(missing);
        }
        return addDoc(collectionRef, data).then(
            async (newDocRef) => {
                const current = getNew({ ...data, id: newDocRef.id });
                await commit({ current, action: 'add', message });
                return updateDoc(newDocRef, current.toFirestore()).then(() =>
                    setModalIsOpen(undefined)
                );
            },
            (error) => {
                console.error('addDoc error', error);
                setModalIsOpen(undefined);
            }
        );
    };
    const onDelete = async () => {
        return setConfirmProps({
            title: `You are about to delete ${selectedRecords.length} ${name} record${
                selectedRecords.length === 1 ? '' : 's'
            }`,
            text: `${selectedRecords.length === 1 ? 'This' : 'These'} ${name} record${
                selectedRecords.length === 1 ? '' : 's'
            } will be deleted, and contracts that contain ${
                selectedRecords.length === 1 ? 'it' : 'them'
            } will be updated. Are you sure you want to continue?`,
            onClick: async (message) => {
                selectedRecords.map(async (recordId) => {
                    const current = records?.find((r) => r.id === recordId) as T;
                    await commit({ current, action: 'delete', message: message });
                    return deleteDoc(doc(collectionRef, recordId));
                });
                setConfirmProps(undefined);
            },
            setModalIsOpen: () => setConfirmProps(undefined),
            actionLabel: 'continue',
        });
    };
    const onDeleteItem = (record: T) => {
        return setConfirmProps({
            title: `You are about to delete this ${name}`,
            text: `This ${name} will be deleted, and contracts that contain it will be updated. Are you sure you want to continue?`,
            onClick: async (message) => {
                const current = records?.find((r) => r.id === record.id) as T;
                await commit({ current, action: 'delete', message });
                return deleteDoc(doc(collectionRef, record.id)).then(() =>
                    setConfirmProps(undefined)
                );
            },
            setModalIsOpen: () => setConfirmProps(undefined),
            actionLabel: 'continue',
            withMessage: true,
        });
    };
    const onEditItem = (record: T) => setEditRecord(record);
    const onDuplicateItem = getNew
        ? (record: T) => setModalIsOpen(getNew({ ...record, id: null }))
        : undefined;

    return (
        <div>
            <Modal
                isOpen={!!confirmProps}
                style={customStyles()}
                onRequestClose={() => setConfirmProps(undefined)}
            >
                {confirmProps ? <Confirm {...confirmProps} /> : null}
            </Modal>
            <Modal
                isOpen={!!reviewIsOpen}
                style={customStyles(2)}
                onRequestClose={() => setReviewIsOpen(false)}
            >
                <SendToReview
                    selectedClauses={
                        selectedRecords.map((id) =>
                            records?.find((r) => r.id === id)
                        ) as unknown as Clause[]
                    }
                    setModalIsOpen={setReviewIsOpen}
                    onUpdate={onUpdate}
                />
            </Modal>
            {actions ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {headers === 'actions' ? (
                        <div>
                            <OrderBy
                                columns={columns}
                                onFieldClick={onFieldClick}
                                headers={headers}
                            />{' '}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {historyTo && (
                            <NavLink to={historyTo} className="plainNavLink">
                                <div
                                    className="historyButton"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        borderRadius: '8px',
                                        padding: '1px 5px',
                                    }}
                                >
                                    <RestoreIcon />
                                    <div style={{ marginLeft: '5px' }}>Change History</div>
                                </div>
                            </NavLink>
                        )}
                        {columns.find((c) => c.fieldType === 'reviewStatus') && (
                            <Button
                                style={{ width: '125px', marginLeft: '25px' }}
                                onClick={() => selectedRecords.length && setReviewIsOpen(true)}
                                theme={selectedRecords.length ? undefined : 'disabled'}
                                title={selectedRecords.length ? '' : 'Select records review'}
                            >
                                Send to Review
                            </Button>
                        )}
                        {addTo ? (
                            <NavLink to={addTo} className="plainNavLink">
                                <Button style={{ width: '186px' }}>Create new contract</Button>
                            </NavLink>
                        ) : getNew ? (
                            <Button
                                style={{ width: '97px', marginLeft: '25px' }}
                                onClick={() => setModalIsOpen(getNew({ tenantId: tenant }))}
                            >
                                Add
                            </Button>
                        ) : null}
                        {getNew && modalIsOpen ? (
                            <Modal
                                isOpen={!!modalIsOpen}
                                style={customStyles(2)}
                                onRequestClose={() => setModalIsOpen(undefined)}
                            >
                                <Create
                                    initialState={modalIsOpen}
                                    label={label}
                                    setModalIsOpen={setModalIsOpen}
                                    onSave={onSave}
                                    columns={columns.filter((column) => column.input)}
                                    name={name}
                                />
                            </Modal>
                        ) : null}
                        {onUpdate ? (
                            <Modal
                                isOpen={!!editRecord}
                                style={customStyles()}
                                onRequestClose={() => setModalIsOpen(undefined)}
                            >
                                {editRecord ? (
                                    <Create
                                        initialState={editRecord}
                                        label={label}
                                        name={name}
                                        setModalIsOpen={(b?: T) =>
                                            setEditRecord(b ? editRecord : undefined)
                                        }
                                        onSave={onUpdate}
                                        columns={columns.filter((column) => column.input)}
                                    />
                                ) : null}
                            </Modal>
                        ) : null}
                    </div>
                </div>
            ) : null}
            {headers === 'columns' ? (
                <OrderBy columns={columns} onFieldClick={onFieldClick} headers={headers} />
            ) : null}
            {list({
                columns,
                records,
                selectedRecords,
                setSelectedRecords,
                recordsState,
                setRecordsState,
                onDeleteItem,
                onEditItem,
                onDuplicateItem,
            })()}
            <div className="listActions">
                {withDelete ? (
                    <div
                        onClick={selectedRecords.length ? onDelete : () => null}
                        className={selectedRecords.length ? 'listAction' : ''}
                    >
                        Delete Selection
                    </div>
                ) : (
                    <div></div>
                )}
                {nextTo ? (
                    <NavLink to={nextTo} className="plainNavLink">
                        <Button style={{ width: '97px' }}>Next</Button>
                    </NavLink>
                ) : null}
            </div>
        </div>
    );
};
CollectionList.defaultProps = {
    list: renderList,
    withDelete: true,
    actions: true,
    headers: 'columns',
    name: '',
};
